import _ from 'lodash'
import { Container, Divider } from '@material-ui/core'
import React, { useState } from 'react'
import { useReactiveVar } from '@apollo/client'
import { projectVar } from '../../../apollo/reactive-vars'
import {
  CoolingParameters,
  DomesticHotWaterParameters,
  ElectricityConsumptionParameters,
  EnergySystemType,
  HeatLoadParameters,
  HourSeriesFragment,
  HourSeriesType,
  Maybe,
  SpaceHeatingParameters,
  UpdateBuildingEnergyModelInput,
  UpdateEnergySystemInput,
  UpdateScheduleInput,
  VentilationParameters,
  type BuildingEnergyModelFragment,
} from '../../../generated/graphql'
import { useBuildingEnergyModels } from '../../../building-energy-model/use-building-energy-models'
import { useEnergySystems } from '../../../energy-system/use-energy-systems'
import { useSchedules } from '../../../schedule/use-schedules'
import { CalibrationInputsLayout } from './CalibrationInputLayout'
import { CalibrationGraphsPage } from './CalibrationGraphsPage'
import { CalibrationDataDialog } from './dialog/CalibrationDataDialog'
import {
  CalibrationFormValues,
  defaultCalibrationValues,
  mapCalibrationFormValuesToInput,
} from './dialog/calibration-form-values'

type Props = {
  model: BuildingEnergyModelFragment
  closeDrawer: () => void
}

export function CalibrationDrawerContent({
  model,
  closeDrawer,
}: Props): React.ReactElement {
  const [isCalibrationEditing, setIsCalibrationEditing] = useState(true)

  const [updatedModel, setUpdatedModel] = useState(model)

  const [updatedEnergySystems, setUpdatedEnergySystems] = useState<
    EnergySystemType[]
  >([])

  const { updateBuildingEnergyModel } = useBuildingEnergyModels()

  const { updateEnergySystem } = useEnergySystems()

  const { updateSchedule } = useSchedules()

  const project = useReactiveVar(projectVar)

  type UpdateEsSystemInput = {
    name: string
    type: EnergySystemType
    scheduleId?: Maybe<string>
    parameters:
      | DomesticHotWaterParameters
      | VentilationParameters
      | CoolingParameters
      | SpaceHeatingParameters
      | HeatLoadParameters
      | ElectricityConsumptionParameters
  }

  const toUpdateEsSystemInput = (
    input: UpdateEnergySystemInput,
    esType: EnergySystemType,
  ): UpdateEsSystemInput => {
    const result: UpdateEsSystemInput = {
      name: input.name,
      type: input.type,
      scheduleId: input.scheduleId,
      parameters: {} as DomesticHotWaterParameters,
    }

    switch (esType) {
      case EnergySystemType.DomesticHotWater:
        result.parameters = input.parameters
          .domesticHotWater as DomesticHotWaterParameters
        break
      case EnergySystemType.SpaceHeating:
        result.parameters = input.parameters
          .spaceHeating as SpaceHeatingParameters
        break
      case EnergySystemType.HeatLoad:
        result.parameters = input.parameters.heatLoad as HeatLoadParameters
        break
      case EnergySystemType.ElectricityConsumption:
        result.parameters = input.parameters
          .electricityConsumption as ElectricityConsumptionParameters
        break
      case EnergySystemType.Ventilation:
        result.parameters = input.parameters
          .ventilation as VentilationParameters
        break
      case EnergySystemType.Cooling:
        result.parameters = input.parameters.cooling as CoolingParameters
        break

      default:
        break
    }

    return result
  }

  const onUpdate = (
    esInput: UpdateEnergySystemInput,
    scheduleId?: string,
    scheduleInput?: UpdateScheduleInput,
  ) => {
    // Update energy system data

    if (!esInput.type) return

    const tempModel = _.cloneDeep(updatedModel)

    const es = tempModel.energySystems.find((s) => s.type === esInput.type)

    if (!es) return

    const input = toUpdateEsSystemInput(esInput, esInput.type)

    _.merge(es, input)

    // Update schedule data
    if (scheduleId && scheduleInput) {
      let esSchedule = es.schedule
      // Ventilation system needs to be handled differently
      if (esInput.type === EnergySystemType.Ventilation)
        esSchedule = tempModel.schedules.find((s) => s.id === scheduleId)

      if (esSchedule) _.merge(esSchedule, scheduleInput)
    }

    setUpdatedEnergySystems([esInput.type])

    setUpdatedModel(tempModel)
  }

  const onDbUpdate = async (
    esId: string,
    esInput: UpdateEnergySystemInput,
    scheduleIds?: string[],
    scheduleInputs?: UpdateScheduleInput[],
  ) => {
    // console.log('DBUPDATE started')

    await updateEnergySystem(esId, esInput)

    if (scheduleIds && scheduleInputs) {
      await Promise.all(
        scheduleIds.map((id, index) =>
          updateSchedule(id, scheduleInputs[index]),
        ),
      )
    }
  }

  const onUpdateModel = async (input: UpdateBuildingEnergyModelInput) => {
    // console.log('BEM saved')
    const tempModel = await updateBuildingEnergyModel(model.id, input)
    setUpdatedModel(tempModel)
  }

  const closeCalibrationDataDialog = () => {
    setIsCalibrationEditing(false)
    if (!updatedModel.calibrationStart) closeDrawer()
  }

  const saveCalibrationData = async (values: CalibrationFormValues) => {
    const input = mapCalibrationFormValuesToInput({ ...values })

    await onUpdateModel(input)

    setIsCalibrationEditing(false)
  }

  const findMeasuredHourSeries = () => {
    const result: HourSeriesFragment[] = []

    if (
      project?.hourSeriesCollections &&
      project.hourSeriesCollections.length > 0
    ) {
      for (let i = 0; i < project.hourSeriesCollections.length; i++) {
        const hsc = project.hourSeriesCollections[i]
        const series = hsc.hourSeries.filter((hs) =>
          [
            HourSeriesType.MeasuredOutdoorTemperature,
            HourSeriesType.MeasuredHeatingDemand,
            HourSeriesType.MeasuredCoolingDemand,
            HourSeriesType.MeasuredElectricityDemand,
          ].includes(hs.type),
        )
        if (series) result.push(...series)
      }
    }
    return result
  }

  return (
    <Container maxWidth={false} disableGutters>
      {updatedModel.calibrationStart && (
        <>
          <CalibrationGraphsPage
            model={updatedModel}
            calibrationYear={new Date(
              updatedModel.calibrationStart,
            ).getFullYear()}
            measuredHourSeries={findMeasuredHourSeries()}
            updatedEnergySystems={updatedEnergySystems}
            onUpdateModel={onUpdateModel}
          />

          <Divider />

          <CalibrationInputsLayout
            model={updatedModel}
            onUpdate={onUpdate}
            onDbUpdate={onDbUpdate}
          />
        </>
      )}
      {!updatedModel.calibrationStart && (
        <CalibrationDataDialog
          open={isCalibrationEditing}
          onSubmit={saveCalibrationData}
          onClose={closeCalibrationDataDialog}
          defaultValues={defaultCalibrationValues}
          measuredHourSeries={findMeasuredHourSeries()}
        />
      )}
    </Container>
  )
}
